import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageDetailLayout } from '@/shared/layout';
import { useApprovalDetail } from './hooks/useApprovals';
import {
  Button,
  Card,
  FeaturePlaceholder,
  Avatar,
  Divider,
  Badge
} from '@/components/ui';
import { ApprovalStatusBadge } from './components/ApprovalStatusBadge';
import { ApprovalTimeline } from './components/ApprovalTimeline';
import { ApprovalActionBar } from './components/ApprovalActionBar';
import { useAuth } from '@/core/auth/AuthContext';
import {
  FileText,
  User,
  Calendar,
  Briefcase,
  Shield,
  Hash,
  Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';

export default function ApprovalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const { detail, isLoading, performAction, isProcessing } = useApprovalDetail(id, profile?.id);

  const [activeTab, setActiveTab] = useState('overview');
  const [comment, setComment] = useState('');

  if (isLoading) return <div className="p-12 text-center text-slate-500 text-sm">Đang tải chi tiết phê duyệt...</div>;
  if (!detail) return <div className="p-12 text-center text-red-500 font-normal">Không tìm thấy thông tin yêu cầu phê duyệt</div>;

  const { request, steps, comments } = detail;

  const handleAction = async (action: 'approve' | 'reject' | 'request_change' | 'cancel') => {
    try {
      await performAction({ action, comment });
      setComment('');
    } catch (error) {
      // toast already handled in hook
    }
  };

  const tabs = [
    { value: 'overview', label: 'Chi tiết yêu cầu' },
    { value: 'steps', label: 'Quy trình duyệt' },
    { value: 'documents', label: 'Tài liệu đính kèm' },
    { value: 'logs', label: 'Nhật ký' },
  ];

  return (
    <PageDetailLayout
      title={request.request_title}
      description={
        <div className="flex items-center gap-3 mt-1.5">
          <ApprovalStatusBadge status={request.status_code} />
          <div className="flex items-center gap-1 text-slate-400 font-mono text-xs">
            <Hash className="w-3 h-3" />
            <span>{request.request_code}</span>
          </div>
          <span className="text-slate-300">|</span>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Briefcase className="w-3.5 h-3.5 text-slate-400" />
            Loại: <span className="font-normal text-slate-700">{request.entity_type}</span>
          </div>
        </div>
      }
      breadcrumbs={[
        { label: 'Phê duyệt', path: '/approvals' },
        { label: 'Chi tiết' }
      ]}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <div className="pb-24">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Content Card */}
              <Card className="p-6 border-slate-200/60 overflow-hidden relative">
                <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                  <FileText className="w-32 h-32" />
                </div>

                <h3 className="font-normal text-slate-900 mb-5 flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  Nội dung yêu cầu
                </h3>

                <div className="space-y-6">
                  <div className="p-5 bg-slate-50/50 rounded-2xl border border-slate-100 min-h-[120px] relative">
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {request.description || 'Không có mô tả chi tiết.'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-5 gap-x-12 px-2">
                    <DetailItem label="Phân hệ" value={request.module_code} icon={Shield} />
                    <DetailItem label="Dự án" value={request.project?.name || '---'} icon={Briefcase} />
                    <DetailItem label="Độ ưu tiên" value={request.priority_code} isBadge priority={request.priority_code} />
                    <div className="flex items-center justify-between py-2.5 border-b border-slate-50">
                      <span className="text-xs font-normal text-slate-500 flex items-center gap-2 tracking-wider">
                        <Eye className="w-3.5 h-3.5" />
                        Liên kết
                      </span>
                      <Button variant="link" className="h-auto p-0 text-xs font-normal text-blue-600 underline decoration-blue-200 underline-offset-4 hover:text-blue-600">
                        Xem {request.entity_type} chi tiết
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Discussion Section */}
              <Card className="p-6 border-slate-200/60">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="font-normal text-slate-900 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-slate-50 flex items-center justify-center">
                      <User className="w-4 h-4 text-slate-500" />
                    </div>
                    Trao đổi & Ý kiến ({comments.length})
                  </h3>
                </div>

                <div className="space-y-8">
                  {comments.map((c) => (
                    <div key={c.id} className="flex gap-4 group">
                      <Avatar name={c.user?.full_name} src={c.user?.avatar_url} size="sm" className="ring-2 ring-white" />
                      <div className="flex-1">
                        <div className="bg-slate-50/80 p-4 rounded-2xl rounded-tl-none border border-slate-100 group-hover:bg-slate-50 transition-colors">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-normal text-slate-900">{c.user?.full_name}</span>
                            <span className="text-[10px] text-slate-400 font-normal">
                              {format(new Date(c.created_at), 'HH:mm - dd/MM/yyyy', { locale: vi })}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 leading-relaxed">{c.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {comments.length === 0 && (
                    <div className="text-center py-12">
                      <div className="w-12 h-12 rounded-full bg-slate-50 flex items-center justify-center mx-auto mb-3">
                        <User className="w-6 h-6 text-slate-300" />
                      </div>
                      <p className="text-sm text-slate-400 italic">Chưa có ý kiến thảo luận nào.</p>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Requester Card */}
              <Card className="p-6 bg-slate-900 text-white -slate-200 border-none relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl" />

                <h4 className="text-xs font-normal tracking-[0.2em] mb-6 flex items-center gap-2 text-slate-400">
                  <Shield className="w-3.5 h-3.5" />
                  Người khởi tạo
                </h4>

                <div className="flex items-center gap-4 mb-8">
                  <Avatar
                    name={request.requester?.full_name}
                    src={request.requester?.avatar_url}
                    size="md"
                    className="ring-2 ring-white/10"
                  />
                  <div>
                    <p className="text-lg font-normal text-white mb-0.5">{request.requester?.full_name}</p>
                    <p className="text-xs text-slate-400 font-normal italic">Employee ID: OS-2024-876</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3.5 bg-white/5 rounded-2xl border border-white/10">
                    <p className="text-[10px] text-slate-400 tracking-widest font-normal mb-1.5">Ngày gửi</p>
                    <p className="text-xs font-normal text-blue-400">
                      {format(new Date(request.requested_at), 'dd/MM/yyyy', { locale: vi })}
                    </p>
                  </div>
                  <div className="p-3.5 bg-white/5 rounded-2xl border border-white/10">
                    <p className="text-[10px] text-slate-400 tracking-widest font-normal mb-1.5">Giờ gửi</p>
                    <p className="text-xs font-normal text-blue-400">
                      {format(new Date(request.requested_at), 'HH:mm', { locale: vi })}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Timeline Card */}
              <Card className="p-6 border-slate-200/60">
                <h4 className="font-normal text-slate-900 mb-8 flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-amber-600" />
                  </div>
                  Lịch trình phê duyệt
                </h4>

                {steps.length > 0 ? (
                  <ApprovalTimeline steps={steps} />
                ) : (
                  <div className="text-center py-8 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                    <p className="text-xs text-slate-400 italic">Quy trình chưa được định nghĩa.</p>
                  </div>
                )}
              </Card>
            </div>
          </div>
        )}

        {(activeTab === 'steps' || activeTab === 'documents' || activeTab === 'logs') && (
          <FeaturePlaceholder
            title={tabs.find(t => t.value === activeTab)?.label || 'Tính năng'}
            description="Dữ liệu đang được đồng bộ từ nhật ký hệ thống, hồ sơ lưu trữ tài liệu và luồng phê duyệt đa cấp thực tế."
          />
        )}
      </div>

      {/* Floating Action Bar */}
      {request.status_code === 'pending' && (
        <ApprovalActionBar
          onApprove={() => handleAction('approve')}
          onReject={() => handleAction('reject')}
          onRequestChange={() => handleAction('request_change')}
          onCancel={() => handleAction('cancel')}
          isProcessing={isProcessing}
        />
      )}
    </PageDetailLayout>
  );
}

function DetailItem({ label, value, icon: Icon, isBadge = false, priority = '' }: any) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-50 group">
      <span className="text-xs font-normal text-slate-500 flex items-center gap-2 tracking-wider group-hover:text-slate-900 transition-colors">
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-500 transition-colors" />}
        {label}
      </span>
      {isBadge ? (
        <Badge
          className={`text-[10px] font-normal px-2.5 py-0.5 rounded-full border-none  ${
            priority === 'urgent' ? 'bg-rose-100 text-rose-600' :
            priority === 'high' ? 'bg-orange-100 text-orange-600' :
            'bg-slate-100 text-slate-700'
          }`}
        >
          {value}
        </Badge>
      ) : (
        <span className="text-sm font-normal text-slate-800 tracking-tight">{value}</span>
      )}
    </div>
  );
}
